import { MEASUREMENT_CATALOG } from '../pages/reports/measurementCatalog';

export const normaliseReportLabel = (value: string) =>
  value.replace(/[‘’`´]/g, "'").replace(/\s+/g, ' ').trim().toLowerCase();

// Express templates use short clinical print labels while structured measurements
// use canonical ids. This is a report-display translation, not an SR alias table.
const REPORT_LABEL_ALIASES: Record<string, string> = {
  'ivsw d': 'IVSd', ivswd: 'IVSd', 'ef biplane': 'LV_EF', lvef: 'LV_EF',
  lvm: 'LV_mass', lvmi: 'LVMI', "rvs'": 'S_prime', rvs: 'S_prime',
  'rv base': 'RV_basal', 'la area': 'LA_area', 'la vol ind': 'LAVi',
  'la vol index': 'LAVi', 'ra area': 'RA_area', 'av pg': 'AV_peakPG',
  'av mg': 'AV_meanPG', 'ava vti': 'AVA_continuity',
  dpi: 'DVI', 'lvot diam': 'LVOT_diam', 'aorta stj': 'Ao_STJ',
  'asc aorta': 'Ao_asc', arch: 'Ao_arch', 'mva (pht)': 'MVA_PHT',
  'mv a vel': 'MV_A', 'mv e vel': 'MV_E', 'decel time': 'DT',
  "e' sept": 'e_prime_septal', "e' lateral": 'e_prime_lateral',
  "avg e/e'": 'E_e_prime', 'avg e/e': 'E_e_prime', 'tr vmax': 'TR_Vmax', ivc: 'IVC_diam',
  'qp:qs': 'QP_QS', 'qp/qs': 'QP_QS',
};

const CATALOG_BY_LABEL = new Map<string, string>();
MEASUREMENT_CATALOG.forEach(entry => {
  [entry.shortLabel, entry.label].filter(Boolean).forEach(label => {
    const key = normaliseReportLabel(label);
    if (!CATALOG_BY_LABEL.has(key)) CATALOG_BY_LABEL.set(key, entry.measurementId);
  });
});

export interface ReportMeasurementCell {
  measurementId: string;
  /** Null when the printed value is a bound such as ">60%", not a number. */
  value: number | null;
  unit: string;
}

/**
 * Reads a "Label: value unit" report cell. The label must match a known label exactly —
 * a substring match turned "Patient ID" into PA and "RVSP" into RV S'. The label ends at
 * the first colon followed by a number, so "QP:QS: 1.0" keeps "qp:qs".
 */
export function parseReportMeasurementCell(cellText: string): ReportMeasurementCell | null {
  const text = normaliseReportLabel(cellText);
  const match = text.match(/^(.+?):\s*([<>]?)\s*([-+]?\d+(?:\.\d+)?)\s*(.*)$/);
  if (!match) return null;
  const label = match[1].trim();
  const measurementId = REPORT_LABEL_ALIASES[label] || CATALOG_BY_LABEL.get(label);
  if (!measurementId) return null;
  return {
    measurementId,
    value: match[2] ? null : Number(match[3]),
    unit: match[4].trim().split(' ')[0],
  };
}

export function measurementIdForReportLabel(cellText: string): string | null {
  return parseReportMeasurementCell(cellText)?.measurementId || null;
}

/** Units compare equal across print variants: "cm²" / "cm2", "mmHg" / "mmhg". */
export const normaliseUnit = (unit?: string | null) =>
  String(unit || '').replace(/²/g, '2').replace(/³/g, '3').replace(/\s+/g, '').toLowerCase();
